
import React from 'react';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import RateCard from './RateCard';
import { FormData } from './types';
import html2canvas from 'html2canvas';
import { saveWithAuth } from '@/utils/authUtils';
import { useNavigate } from 'react-router-dom';

interface ResultsStepProps {
  formData: FormData;
  calculations: {
    hourlyRate: number;
    totalIncome: number;
    totalMonthlyExpenses: number;
    effectiveHours: number;
  };
  onStartOver: () => void;
}

const ResultsStep = ({ formData, calculations, onStartOver }: ResultsStepProps) => {
  const navigate = useNavigate();

  const handleSaveToProfile = async () => {
    // Use our utility function for saving with auth
    const success = await saveWithAuth(
      {
        formData,
        calculations
      },
      async (data, session) => {
        // Prepare data for insert - convert to JSON compatible format
        const rateData = {
          user_id: session.user.id,
          salary: parseFloat(formData.salary.replace(/,/g, '')),
          // Convert expenses object to a JSON-compatible format
          expenses: JSON.parse(JSON.stringify(formData.expenses)),
          // Convert timeOff object to a JSON-compatible format
          time_off: JSON.parse(JSON.stringify(formData.timeOff)),
          calculations: {
            hourlyRate: calculations.hourlyRate,
            totalIncome: calculations.totalIncome,
            totalMonthlyExpenses: calculations.totalMonthlyExpenses,
            effectiveHours: calculations.effectiveHours,
          },
          crystal_ball_mode: false,
        };
        
        // Save rate data to Supabase
        const { error } = await supabase
          .from('rate_calculations')
          .insert(rateData);
        
        if (error) {
          console.error('Error saving rate calculation:', error);
          throw error;
        }
      },
      () => {
        // Auth required callback
        navigate('/auth?redirect=/rate-whisperer');
      }
    );
    
    if (success) {
      toast.success("Your rate has been saved to your profile");
    }
  };

  const handleDownloadRateCard = async () => {
    const rateCardElement = document.querySelector('.rate-card') as HTMLElement;
    if (!rateCardElement) return;

    try {
      const canvas = await html2canvas(rateCardElement);
      const link = document.createElement('a');
      link.download = 'rate-card.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error generating rate card:', error);
      toast.error("Failed to download rate card. Please try again.");
    }
  };

return (
    <div className="space-y-10 animate-fade-in">
      <div className="rate-card">
        <RateCard
          hourlyRate={calculations.hourlyRate}
          totalIncome={calculations.totalIncome}
          monthlyExpenses={calculations.totalMonthlyExpenses}
          billableHours={calculations.effectiveHours}
        />
      </div>

      {/* Action Buttons */}
      <div className="space-y-4">
        <Button 
          onClick={() => window.location.href = 'https://loadedcareer.com'}
          className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-primary to-secondary hover:from-primary-hover hover:to-secondary-hover transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] rounded-xl"
        >
          <span className="mr-2">🚀</span>
          Go To Loaded For More
        </Button>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Button 
            variant="outline" 
            onClick={handleSaveToProfile}
            className="py-4 bg-card-bg border-2 border-primary/30 hover:border-primary hover:bg-primary/10 transition-all duration-300 rounded-xl"
          >
            <span className="mr-2">💾</span>
            Save to Profile
          </Button>

          <Button 
            variant="outline" 
            onClick={handleDownloadRateCard}
            className="py-4 bg-card-bg border-2 border-accent-cyan/30 hover:border-accent-cyan hover:bg-accent-cyan/10 transition-all duration-300 rounded-xl"
          >
            <span className="mr-2">📥</span>
            Download Card
          </Button>
        </div>

        <Button 
          variant="outline" 
          onClick={onStartOver}
          className="w-full py-4 bg-card-bg border-2 border-border hover:border-text-secondary/50 hover:bg-card-hover transition-all duration-300 rounded-xl text-text-secondary hover:text-text-primary"
        >
          <span className="mr-2">🔄</span>
          Start Over
        </Button>
      </div>
    </div>
  );
};

export default ResultsStep;
